import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import { Spinner } from '@fluentui/react-components';
import { Controller, useForm } from 'react-hook-form';
import { CoherencePanelStyles } from '../../../app/common/styles/CommonStyleObjects';
import ActionButtons from '../../common/ActionButtons';
import LegoCategoryDropdown from '../../common/formFields/LegoCategoryDropdown';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import {
  GET_ENTITY_TERMINATION_LEGAL_REVIEW,
  MOVE_ENTITY_TERMINATION_TO_BLOCKER,
} from '../../../utils/statApi/EntityTerminationApi';
import { GET_TERMINATION_LIFECYCLE_STATUSES_QUERY } from '../../../utils/statApi/EntityStatusApi';
import { TERMINATION_LIFECYCLE_STATUS_BLOCKER } from '../../../app/common/constants/EntityStatusConstants';
import IDropdownReference from '../../../utils/types/IDropdownReference';

interface IMoveToBlockerPanelProps {
  companyCode: string;
  closePanel: () => void;
}

interface IMoveToBlockerForm {
  legoCategory?: IDropdownReference;
}

const MoveToBlockerPanel: React.FC<IMoveToBlockerPanelProps> = ({ companyCode, closePanel }) => {
  const { handleSubmit, control, errors, setValue } = useForm<IMoveToBlockerForm>();

  const { data: lifecycleData, loading: lifecycleLoading } = useQuery(
    GET_TERMINATION_LIFECYCLE_STATUSES_QUERY,
  );

  const blockerLifecycleStatus = lifecycleData?.terminationLifecycleStatuses?.find(
    (s: IDropdownReference) => s.fieldName === TERMINATION_LIFECYCLE_STATUS_BLOCKER,
  );

  const [moveToBlocker, { loading, error }] = useMutation(MOVE_ENTITY_TERMINATION_TO_BLOCKER, {
    refetchQueries: [
      {
        query: GET_ENTITY_TERMINATION_LEGAL_REVIEW,
        variables: { companyCode },
      },
    ],
    onCompleted: () => {
      closePanel();
    },
  });

  const handleSave = (formData: IMoveToBlockerForm): void => {
    moveToBlocker({
      variables: {
        input: {
          companyCode,
          legoCategoryId: formData.legoCategory?.id,
        },
      },
    });
  };

  return (
    <CoherencePanel
      panelSize={CoherencePanelSize.medium}
      titleText="Move to Blocker"
      isOpen
      onDismiss={closePanel}
      styles={CoherencePanelStyles}
      hasCloseButton
      onRenderFooter={(): JSX.Element => (
        <ActionButtons
          closePanel={closePanel}
          saveLabel="Move to Blocker"
          saveTitle="Move to Blocker"
          cancelLabel="Cancel"
          cancelTitle="Cancel"
          handleSubmit={handleSubmit(handleSave)}
          mutationLoading={loading}
        />
      )}
    >
      <LoadingErrorMessage loading={loading} error={error} actionName="moving entity to Blocker" />
      {lifecycleLoading ? (
        <Spinner label="Loading..." />
      ) : (
        <Controller
          control={control}
          name="legoCategory"
          rules={{ required: 'Blocker category is required.' }}
          render={({ onChange, value }): React.ReactElement => (
            <LegoCategoryDropdown
              label="Blocker Category"
              required
              errors={errors}
              value={value}
              handleChange={(val: IDropdownReference) => {
                onChange(val);
                setValue('legoCategory', val);
              }}
              valueKey="legoCategory"
              outputKey="legoCategory"
              watchTerminationLifecycleStatus={blockerLifecycleStatus}
              hideNonSelectable
            />
          )}
        />
      )}
    </CoherencePanel>
  );
};

export default MoveToBlockerPanel;
